import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  useTheme
} from '@mui/material';
import WeekViewCalendar from './WeekViewCalendar';
import UnavailableBlockEditDialog from './UnavailableBlockEditDialog';
import { BorderBeam } from '../ui/BorderBeam';
import { UnavailableBlock } from '../../models/calendar';

interface UnavailableBlockDialogProps {
  open: boolean;
  onClose: () => void;
  selectedDate: Date | null;
  practitionerId?: string;
  practitioners: any[];
  onBlockCreated: () => void;
}

const UnavailableBlockDialog: React.FC<UnavailableBlockDialogProps> = ({
  open,
  onClose,
  selectedDate,
  practitionerId: defaultPractitionerId,
  practitioners,
  onBlockCreated
}) => {
  const theme = useTheme();
  const [selectedPractitionerId, setSelectedPractitionerId] = useState<string | undefined>(defaultPractitionerId);
  
  // State for edit dialog
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [selectedBlock, setSelectedBlock] = useState<UnavailableBlock | null>(null);

  // Update selected practitioner when props change
  useEffect(() => {
    if (defaultPractitionerId) {
      setSelectedPractitionerId(defaultPractitionerId);
    } else if (practitioners.length > 0) {
      setSelectedPractitionerId(practitioners[0].id);
    }
  }, [defaultPractitionerId, practitioners]);

  // Handle block click for editing
  const handleBlockClick = (block: UnavailableBlock) => {
    setSelectedBlock(block);
    setIsEditDialogOpen(true);
  };

  // Handle closing edit dialog
  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setSelectedBlock(null);
  };

  // Handle block update/delete
  const handleBlockUpdated = () => {
    onBlockCreated(); // Refresh the calendar
  };

  const handleBlockDeleted = () => {
    onBlockCreated(); // Refresh the calendar
  };

  // If the dialog is open, show the week view
  if (open) {
    // Log the practitioner info for debugging
    console.log("UnavailableBlockDialog - Practitioner Info:", {
      defaultPractitionerId,
      selectedPractitionerId,
      selectedDate,
      practitioners
    });
    
    // Find practitioner details if available
    const practitioner = practitioners.find(p => p.id === selectedPractitionerId);
    console.log("Selected practitioner:", practitioner);
    
    return (
      <>
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.15)',
              overflow: 'visible'
            }
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <BorderBeam 
              size={80}
              duration={5}
              colorFrom={theme.palette.primary.main}
              colorTo={theme.palette.secondary.main}
            />
            <Box sx={{ p: 2 }}>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  Manage Unavailable Time Blocks
                </Typography>
                <FormControl variant="outlined" sx={{ minWidth: 200 }}>
                  <InputLabel id="practitioner-select-label">Practitioner</InputLabel>
                  <Select
                    labelId="practitioner-select-label"
                    id="practitioner-select"
                    value={selectedPractitionerId || ''}
                    onChange={(e) => setSelectedPractitionerId(e.target.value)}
                    label="Practitioner"
                  >
                    {practitioners.map((practitioner) => (
                      <MenuItem key={practitioner.id} value={practitioner.id}>
                        {practitioner.name || `${practitioner.first_name} ${practitioner.last_name}`.trim()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            <WeekViewCalendar 
              onClose={onClose}
              practitionerId={selectedPractitionerId}
              onBlocksCreated={onBlockCreated}
              initialDate={selectedDate || new Date()}
              onBlockClick={handleBlockClick}
            />
            </Box>
          </Box>
        </Dialog>
        
        {/* Edit dialog */}
        <UnavailableBlockEditDialog
          open={isEditDialogOpen}
          onClose={handleCloseEditDialog}
          block={selectedBlock}
          practitioners={practitioners}
          onBlockUpdated={handleBlockUpdated}
          onBlockDeleted={handleBlockDeleted}
        />
      </>
    );
  }
  
  // If dialog is closed, render nothing
  return null;
};

export default UnavailableBlockDialog;